/**
 * Coregistration requests and notification emails (`app/services/coregistration_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { map, Observable } from 'rxjs';
import { assertPojo } from '@brainfocus/web/api/response';
import type { ListManagedPayload, NeuroimageFileType, CoregistrationStatusValue, CoregistrationResult, NotificationEmail } from '@brainfocus/web/api/types';

// ========== Neuroimage / Coregistration API ==========

/**
 * Create a new coregistration request for a patient
 */
export async function createCoregistration(patientId: number, name?: string): Promise<CoregistrationResult> {
    const body: Record<string, string> = {};
    if (name) {
        body['name'] = name;
    }
    const response = await client.request('POST', `/api/patient/${patientId}/coregistration`, body);
    assertPojo<CoregistrationResult>(response);
    return response;
}

/**
 * Get coregistration status for a patient
 */
export async function getCoregistrationStatus(patientId: number): Promise<CoregistrationStatusResponse> {
    const response = await client.request('GET', `/api/patient/${patientId}/coregistration-status`);
    assertPojo<CoregistrationStatusResponse>(response);
    return response;
}

/**
 * Upload coregistration results for a specific coregistration request (SuperAdmin only)
 */
export function uploadCoregistrationResults(
    payload: UploadCoregistrationPayload,
): Observable<client.ResponseProgress<UploadCoregistrationResult>> {
    const formData: Record<string, File | string> = {
        alignedMri: payload.alignedMri,
        coordinates: payload.coordinates,
    };
    if (payload.mriName) {
        formData['mriName'] = payload.mriName;
    }
    return client.requestWithProgress<{ mriRecordId: number; coregistration: CoregistrationResult }>(
        `/api/coregistration/${payload.coregistrationId}/upload-results`,
        formData,
    ).pipe(
        map((v) => {
            if (v.result) {
                return { result: v.result };
            } else {
                return { progress: v.progress };
            }
        }),
    );
}

/**
 * Update coregistration status (SuperAdmin only)
 */
export async function updateCoregistrationStatus(
    coregistrationId: number,
    status: CoregistrationStatusValue,
): Promise<CoregistrationResult> {
    const response = await client.request('PATCH', `/api/coregistration/${coregistrationId}/status`, { status });
    assertPojo<CoregistrationResult>(response);
    return response;
}

/**
 * Get all coregistration requests for admin panel (SuperAdmin only)
 */
export async function getCoregistrationRequests(payload: ListManagedPayload & { statusFilter?: 'pending' | 'completed' }): Promise<CoregistrationRequest[]> {
    const response = await client.request('POST', '/admin/get-coregistrations', {
        offset: payload.offset,
        limit: payload.limit,
        status_filter: payload.statusFilter ?? 'pending',
    });
    assertPojo<{ coregistrations: CoregistrationRequest[] }>(response);
    return response.coregistrations;
}

/**
 * Update coregistration status to in_progress (SuperAdmin only)
 */
export async function markCoregistrationInProgress(coregistrationId: number): Promise<void> {
    await client.request('POST', '/admin/update-coregistration-status', {
        coregistration_id: coregistrationId,
        status: 'in_progress',
    });
}

/**
 * Delete coregistration request and all associated neuroimage files
 * (uploader of neuroimage files or SuperAdmin)
 */
export async function deleteCoregistration(coregistrationId: number): Promise<void> {
    await client.request('DELETE', `/api/coregistration/${coregistrationId}`);
}

/**
 * Get notification email recipients for coregistration requests (SuperAdmin only)
 */
export async function getNotificationEmails(): Promise<NotificationEmail[]> {
    const response = await client.request('POST', '/admin/get-notification-emails', {});
    assertPojo<{ emails: NotificationEmail[] }>(response);
    return (response as { emails: NotificationEmail[] }).emails;
}

/**
 * Add a notification email recipient (SuperAdmin only)
 */
export async function addNotificationEmail(email: string): Promise<NotificationEmail> {
    const response = await client.request('POST', '/admin/add-notification-email', { email });
    assertPojo<NotificationEmail>(response);
    return response as NotificationEmail;
}

/**
 * Delete a notification email recipient (SuperAdmin only)
 */
export async function deleteNotificationEmail(id: number): Promise<void> {
    await client.request('POST', '/admin/delete-notification-email', { id });
}

export interface CoregistrationStatusItem extends CoregistrationResult {
    hasPreMri: boolean;
    hasPostCtMri: boolean;
    hasRequiredInputs: boolean;
    hasNeuroimageFiles: boolean;
}

export interface CoregistrationStatusResponse {
    coregistrations: CoregistrationStatusItem[];
    activeCount: number;
    /** @deprecated Use coregistrations array instead */
    coregistration: CoregistrationResult | null;
    /** @deprecated Use coregistrations array instead */
    hasNeuroimageFiles: boolean;
}

export interface UploadCoregistrationPayload {
    coregistrationId: number;
    alignedMri: File;
    coordinates: File;
    mriName?: string;
}

export interface UploadCoregistrationResult {
    mriRecordId: number;
    coregistration: CoregistrationResult;
}

/**
 * Coregistration request as returned by admin endpoint
 */
export interface CoregistrationRequest {
    coregistrationId: number;
    patientId: number;
    patientName: string;
    patientDeleted?: boolean;
    institutionName: string;
    institutionId: number;
    status: CoregistrationStatusValue;
    files: CoregistrationFile[];
    createdAt: string | null;
    // Name given by the user who requested the coregistration (for pending requests)
    name?: string | null;
    // Fields only present for completed coregistrations
    requesterName?: string;
    completedAt?: string | null;
}

export interface CoregistrationFile {
    id: number;
    filename: string;
    fileType: NeuroimageFileType;
    uploadedAt: string | null;
    uploaderName: string;
}
